import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useRef } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import type { TableContext } from '@/api/types';
import { resolveTable } from '@/auth/actions';
import type { GuestSession } from '@/auth/guest-session';
import { ErrorState } from '@/components/ErrorState';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import type { Money } from '@/lib/money';
import type { Cart } from './api';

interface CustomerContextValue {
  session: GuestSession;
  table: TableContext;
  /** Wraps a server amount with the branch currency. No arithmetic. */
  money: (amount: number) => Money;
  /** The cart as the customer last saw or changed it; used to explain changes made by someone else. */
  lastSeenCart: MutableRefObject<Cart | null>;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

export const tableContextKey = (qrToken: string) => ['table-context', qrToken] as const;

/** Venue details for a QR token (public endpoint). The branch currency comes from here. */
export function useTableContext(qrToken: string) {
  return useQuery({
    queryKey: tableContextKey(qrToken),
    queryFn: () => resolveTable(qrToken),
    staleTime: 5 * 60_000,
  });
}

/** Loads the venue details every customer screen needs (currency, names) before showing them. */
export function CustomerProvider({
  session,
  children,
  fallback = (content) => content,
}: {
  session: GuestSession;
  children: ReactNode;
  /** Wraps the loading and error states (they render before the venue details exist). */
  fallback?: (content: ReactNode) => ReactNode;
}) {
  const table = useTableContext(session.qrToken);
  const lastSeenCart = useRef<Cart | null>(null);
  const currency = table.data?.branch_currency;

  const value = useMemo<CustomerContextValue | null>(
    () =>
      table.data && currency
        ? {
            session,
            table: table.data,
            money: (amount) => ({ amount, currency }),
            lastSeenCart,
          }
        : null,
    [session, table.data, currency],
  );

  if (value) return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
  if (table.isError) {
    return fallback(<ErrorState error={table.error} onRetry={() => void table.refetch()} />);
  }
  return fallback(
    <LoadingRegion>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    </LoadingRegion>,
  );
}

export function useCustomer(): CustomerContextValue {
  const value = useContext(CustomerContext);
  if (!value) throw new Error('useCustomer must be used inside <CustomerProvider>.');
  return value;
}
